import { useEffect, useState } from 'react';
import { PageLayout } from '../../PageLayout';
import { Tab, Tabs, useTheme } from '@mui/material';
import { PortalUserData } from '../../../types';
import { managesTeam, getUser, isAdmin, isSupport } from '../../../store/User';
import { getPortalUserSubject, usePortalUsers } from '../../../store/PortalUser';
import { TeamTab } from './Tabs/TeamTab';
import { Team, getTeamsSubject, useTeamsStore } from '../../../store/Team';
import { useAccountCustomizationsStore } from '../../../store/AccountCustomization';
import { useRoles } from '../../../store/Role';
import { useAccounts } from '../../../store/Accounts';
import { UserTab } from './Tabs/UserTab';
import { AccountsTab } from './Tabs/AccountsTab';
import { getGeneralStoreSubject, updateState } from '../../../store/GeneralStore';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TabPanel } from '../../Atoms/TabPanel';
import { defaultFeatureFlags, FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { SupportTab } from './Tabs/SupportTab';
import { AssetConverterTab } from './Tabs/AssetConverterTab';
import { McpAccessTab } from './Tabs/McpAccessTab';
import { ProjectManagementConfigTab } from './Tabs/ProjectManagementConfigTab';
import { PermissionSetsTab } from './Tabs/PermissionSetsTab';
import { BlockListTab } from './Tabs/BlockListTab';
import { getEligibleOptionalTabs, reconcileSelectedTab } from './getEligibleOptionalTabs';

type AdminLoadingState = { users: boolean; teams: boolean; accounts: boolean };
const defaultLoadingState = { users: false, teams: false, accounts: false };

export const AdminPage = () => {
  const user = getUser();
  const page = managesTeam(user) ? 'Team Settings' : 'Admin';
  const { getPortalUsers } = usePortalUsers();
  const { fetchTeams } = useTeamsStore();
  const { fetchRoles } = useRoles();
  const { fetchAccounts } = useAccounts();
  const [portalUsers, setPortalUsers] = useState<PortalUserData[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const { fetchAccountCustomizations } = useAccountCustomizationsStore();
  const [selectedTab, setSelectedTab] = useState(0);
  const [adminLoading, setAdminLoading] = useState<AdminLoadingState>(defaultLoadingState);
  const theme = useTheme();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);

  const [tabs, setTabs] = useState(
    managesTeam(user) ? ['Users'] : isAdmin(user) || isSupport(user) ? ['Users', 'Teams', 'Accounts'] : ['Accounts']
  );

  const getComponent = (name: string) => {
    switch (name) {
      case 'Users':
        return <UserTab portalUsers={portalUsers} />;
      case 'Teams':
        return <TeamTab teams={teams} />;
      case 'Accounts':
        return <AccountsTab />;
      case 'Support':
        return <SupportTab />;
      case 'Asset Converter':
        return <AssetConverterTab />;
      case 'MCP Access':
        return <McpAccessTab />;
      case 'Project Management Configuration':
        return <ProjectManagementConfigTab />;
      case 'Permission Sets':
        return <PermissionSetsTab />;
      case 'Block List':
        return <BlockListTab />;
      default:
        return null;
    }
  };

  const isTabLoading = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return managesTeam(user) || isAdmin(user) || isSupport(user) ? adminLoading.users : adminLoading.accounts;
      case 1:
        return adminLoading.teams;
      case 2:
        return adminLoading.accounts;
      default:
        return false;
    }
  };

  const initAdminData = async () => {
    setAdminLoading({ users: true, teams: true, accounts: false });
    await Promise.all([
      fetchAccounts(),
      fetchRoles(),
      fetchAccountCustomizations(),
      getPortalUsers().then(() => setAdminLoading((state) => ({ ...state, users: false })))
    ]);

    // Wait to fetch teams, depends on accounts.
    await fetchTeams().then(() => setAdminLoading((state) => ({ ...state, teams: false })));
  };

  const initAccountsData = async () => {
    setAdminLoading({ users: false, teams: false, accounts: false });
    await Promise.all([fetchAccounts()]);
  };

  const initTeamManagerData = async () => {
    setAdminLoading({ users: true, teams: false, accounts: false });
    await Promise.all([
      fetchRoles(),
      fetchAccountCustomizations(),
      getPortalUsers().then(() => setAdminLoading((state) => ({ ...state, users: false })))
    ]);
  };

  useEffect(() => {
    const nextTabs = getEligibleOptionalTabs(tabs, featureFlags, user);
    setTabs(nextTabs);
    setSelectedTab((current) => reconcileSelectedTab(tabs, nextTabs, current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags]);

  useEffect(() => {
    const featureFlagsSub = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));
    const portalUsersSub = getPortalUserSubject().subscribe((portalUsersData) => setPortalUsers(portalUsersData));
    const teamsSub = getTeamsSubject().subscribe((teams) => {
      setTeams(teams);
    });
    const generalStateSub = getGeneralStoreSubject().subscribe(({ admin }) => {
      setSelectedTab(admin.selectedTab);
    });

    managesTeam(user) ? initTeamManagerData() : isAdmin(user) || isSupport(user) ? initAdminData() : initAccountsData();

    return () => {
      if (featureFlagsSub) featureFlagsSub.unsubscribe();
      if (portalUsersSub) portalUsersSub.unsubscribe();
      if (teamsSub) teamsSub.unsubscribe();
      if (generalStateSub) generalStateSub.unsubscribe();
    };
  }, []);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    updateState({ admin: { selectedTab: index } });
    // If switching to Users tab, trigger getPortalUsers if not already loading
    const isUsersTab = tabs[index] === 'Users';
    if (isUsersTab && !adminLoading.users) {
      setAdminLoading((state) => ({ ...state, users: true }));
      getPortalUsers().finally(() => setAdminLoading((state) => ({ ...state, users: false })));
    }
  };

  return (
    <PageLayout
      dataCy="admin-page-card"
      pageTitle={page}>
      <>
        <Tabs
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '1.5rem',
            backgroundColor: 'rgb(247 247 247)',
            borderBottom: '1px solid rgb(229 231 235)',
            borderTop: '1px solid rgb(229 231 235)'
          }}
          TabIndicatorProps={{
            sx: {
              backgroundColor: theme.palette.primary.main,
              height: selectedTab <= tabs.length - 1 ? 2 : 0,
              borderRadius: 1
            }
          }}
          value={selectedTab}
          onChange={(_e, index) => handleTabChange(index)}>
          {tabs.map((name, index) => (
            <Tab
              data-cy={`tab-link-${name}`}
              sx={{ textTransform: 'none', fontSize: '.8rem', color: 'black' }}
              key={index}
              label={name}
            />
          ))}
        </Tabs>
        {isTabLoading(selectedTab) && <ComponentSpinner sx={{ marginTop: '15rem' }} />}
        {!isTabLoading(selectedTab) &&
          tabs.map((name, index) => (
            <TabPanel
              key={`${index}`}
              index={index}
              value={selectedTab}>
              {getComponent(name)}
            </TabPanel>
          ))}
      </>
    </PageLayout>
  );
};
